/**
 * Refunds Service
 *
 * Data access layer for refund operations.
 * Handles fetching refund history and processing refunds via the API.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';

export interface RefundRecord {
  id: string;
  booking_id: string;
  merchant_id: string;
  refund_amount: number;
  refund_type: 'percentage' | 'fixed';
  refund_value: number;
  reason: string | null;
  stripe_refund_id: string | null;
  paypal_refund_id: string | null;
  paypal_capture_id: string | null;
  /**
   * Refund ledger scope. Only 'booking' rows move booking payment_state;
   * 'damage' (damage-charge ledger) and 'unknown' (un-classifiable legacy row
   * pending manual review) are excluded from the payment_state sum.
   */
  scope: 'booking' | 'damage' | 'unknown';
  /** Provenance: 'paypal_webhook' | 'admin_bookkeeping' | 'stripe_api'. */
  source: string | null;
  /**
   * 'confirmed' rows count toward payment_state. 'pending_review' rows are
   * ambiguous provisional rows parked by the storefront webhook for manual
   * reconciliation and are excluded from the sum.
   */
  reconciliation_status: 'confirmed' | 'pending_review';
  is_bookkeeping_only: boolean;
  refunded_by: string | null;
  payment_transaction_id: string | null;
  /**
   * When the refund was issued at the provider (Stripe refund `created`), as
   * opposed to when this row was written. NULL for rows predating the column and
   * for bookkeeping-only rows where the external refund date is unknown — readers
   * that care about the refund's event date use COALESCE(refunded_at, created_at).
   */
  refunded_at: string | null;
  /**
   * TRUE when this refund returned money for items removed from the booking
   * or for an overpayment — the total is or will be reduced separately — so it
   * is added back when computing what the customer still owes. FALSE (the
   * default, and every row before the column existed) means the refund reduced
   * the obligation itself: a cancellation or goodwill refund that never becomes
   * a debt. See migration 20260909000000_add_refund_restores_balance.sql.
   */
  restores_balance: boolean;
  created_at: string;
}

export interface ProcessRefundParams {
  bookingId: string;
  amount: number;
  refundType: 'percentage' | 'fixed';
  refundValue: number;
  reason?: string;
  userId?: string;
  /**
   * When set to 'bookkeeping', the backend skips the Stripe/PayPal refund
   * call and only inserts a refund_history row with is_bookkeeping_only=true.
   * Use this for external refunds that have already been processed outside
   * the admin panel (PayPal dashboard, Stripe dashboard, bank transfer, cash).
   */
  mode?: 'bookkeeping';
  /**
   * Scope the refund to a specific transaction ledger. Required when the
   * booking has both booking_payment and damage_charge transactions, so LIFO
   * doesn't accidentally refund the wrong one. Omit on bookings with a single
   * ledger (the common case) for legacy behaviour.
   */
  transactionType?: 'booking_payment' | 'damage_charge';
  /**
   * Mark the refund as returning money for removed items / an overpayment so it
   * counts back toward the outstanding balance (refund_history.restores_balance).
   * Omit or false for cancellation and goodwill refunds. Ignored for the damage
   * ledger, which never moves the booking balance.
   */
  restoresBalance?: boolean;
}

/**
 * Fetch refund history for a booking
 */
export async function getRefundHistory(bookingId: string) {
  try {
    const { data, error } = await supabase
      .from('refund_history')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return { data: (data || []) as RefundRecord[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Process a refund via the API endpoint
 */
export async function processRefund(params: ProcessRefundParams) {
  try {
    const response = await fetch('/api/stripe/create-refund', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      return { data: null, error: result.message || 'Failed to process refund' };
    }

    // API may return multiple refund records when a refund is split across PIs
    const refunds = (result.refunds || [result.refund]) as RefundRecord[];
    return {
      data: {
        refund: refunds[0],
        refunds,
        newPaymentState: result.newPaymentState as string,
        isBookkeepingOnly: result.isBookkeepingOnly as boolean,
        partialFailure: result.partialFailure as boolean | undefined,
      },
      error: null,
    };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : 'Network error' };
  }
}
